<script setup>
import { computed } from "vue";
import { notifyError, notifySuccess } from "@/utils/notificationUtils";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "../../../utils/googleAnalytics.js";

/** props定義 */
const props = defineProps({
	entity: Object,
	isRenamingMode: Boolean,
});

const isSimaOrDxf = computed(() => {
	const lineType = props.entity?.lineType ?? props.entity?.type;
	return lineType === "sima" || lineType === "dxf";
});

const isLoadedAndVisible = computed(() =>
	Boolean(props.entity?.isLoaded && props.entity?.isVisible),
);

const isExportDisabled = computed(() => !isLoadedAndVisible.value);

/** 関数定義 */
const store = useStore();
const { t } = useI18n();

const deleteLineWorksItem = async () => {
	// Google Analyticsに連携
	addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_LINE_DELETE);

	await store.dispatch("executeWithSpinner", async () => {
		try {
			if (props.entity.lineType === "sima") {
				await store.dispatch("simaLine/deleteSimaLine", props.entity);
			} else if (props.entity.lineType === "arbitrary") {
				await store.dispatch("arbitraryLine/deleteArbitraryLine", props.entity);
			}
			notifySuccess(
				{ $store: store, $t: t },
				`${props.entity.lineName} ${t("REMOVE")} ${t("successful")}`,
			);
		} catch (e) {
			notifyError(
				{ $store: store, $t: t },
				`${props.entity.lineName} ${t("REMOVE")} ${t("failed")}`,
			);
		}
	});
};

const exportLineworks = () => {
	if (isSimaOrDxf.value) {
		return;
	}
	// Export format selection dialog を開く
	const customEvent = new CustomEvent("openExportLineworksFormatDialog", {
		detail: { entity: props.entity },
	});
	window.dispatchEvent(customEvent);
};

const exportDxf = async () => {
	// Google Analyticsに連携
	addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_LINE_CROSS_SECTION_EXPORT);

	await store.dispatch("executeWithSpinner", async () => {
		try {
			await store.dispatch(
				"lineWorksCrossSection/exportCrossSection",
				props.entity,
			);
		} catch (e) {
			console.error(e);
			notifyError(
				{ $store: store, $t: t },
				t("LINEWORKS_CROSS_SECTION_DOWNLOAD_FAILED", {
					name: props.entity.lineName,
				}),
			);
		}
	});
};
</script>

<template>
	<v-list density="compact">
		<!-- 出力 -->
		<v-list-item
			v-if="!isSimaOrDxf"
			@click="exportLineworks"
			class="list-doing"
			:disabled="isExportDisabled"
		>
			<v-icon class="mr-4">icon:Export</v-icon>
			<span class="item-name">{{ $t("EXPORT_AS") }}</span>
		</v-list-item>
		<!-- 断面図出力 -->
		<v-list-item @click="exportDxf" class="list-doing" :disabled="!(entity.isCrossSectionVisible)">
			<v-icon class="mr-4">icon:Export</v-icon>
			<span class="item-name">{{ $t("EXPORT_CROSS_SECTION_DXF") }}</span>
		</v-list-item>
		<!-- 名称変更 -->
		<v-list-item @click="toggleRenamingMode" class="list-doing">
			<v-icon class="mr-4">mdi-rename</v-icon>
			<span class="item-name">{{ $t("RENAME") }}</span>
		</v-list-item>
		<!-- 編集 -->
		<v-list-item
			@click="editLineWorksCrossSection(entity)"
			class="list-doing"
			:disabled="!isLoadedAndVisible"
		>
			<v-icon class="mr-4">icon:Pencil</v-icon>
			<span class="item-name">{{ $t("EDIT_CROSS_SECTION") }}</span>
		</v-list-item>
		<!-- 削除 -->
		<v-list-item @click="deleteLineWorksItem" class="list-delayed">
			<v-icon class="mr-4">icon:Trash-Can</v-icon>
			<span class="item-name">{{ $t("REMOVE") }}</span>
		</v-list-item>
	</v-list>
</template>

<script>
import { mapActions } from "vuex";

export default {
	name: "LineWorksThreeDotMenu",
	methods: {
		...mapActions(['lineWorksCrossSection']),
		async editLineWorksCrossSection(entity) {
			// Google Analyticsに連携
			addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_LINE_EDIT);

			const customLineWorkEditDialogEvent = new CustomEvent("openLineWorksEditDialog", { detail: { crossSectionSettings: entity.crossSectionSetting, lineWorksId: this.entity.viewerLineId } });
			window.dispatchEvent(customLineWorkEditDialogEvent);
		},
		toggleRenamingMode() {
			this.$emit('update:isRenamingMode', !this.isRenamingMode)
		},
	},
}
</script>

<style scoped>
.item-name {
	font-size: 13px;
}
</style>